import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, Star } from "lucide-react";
import { collection, getDocs, limit, query } from "firebase/firestore";
import { DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import GoogleIcon from "@/components/icons/GoogleIcon";
import {
  db,
  getPlatform,
  getUserAccounts,
  logException,
  useAuth,
} from "@um/ui";
import { PersonaImage, TestLoginButton } from "@um/ui/components";
import {
  getPersona,
  PersonaImageCollection,
  type Animal,
  type PersonaImageInfo,
} from "@um/contracts";
import {
  redeemWebPurchaseInDialog,
  resetWebPurchaseRedemptionOutcome,
} from "@/services/paywall";
import { usePaymentsStore } from "@/store/usePaymentsStore";
import { sendConversionEvent } from "@/services/social";

interface WebPurchaseRedemptionDialogContentProps {
  onClose: () => void;
}

type RedemptionAccountOption = {
  id: string;
  name: string;
  email: string;
  animalImage?: PersonaImageInfo | null;
};

type AccountAnimalPreview = {
  name?: string;
  image?: PersonaImageInfo | null;
};

function AnimalPersonaImage({
  image,
  name,
  sizeClass = "h-12 w-12",
}: {
  image?: PersonaImageInfo | null;
  name?: string;
  sizeClass?: string;
}) {
  if (image) {
    return (
      <div
        className={`${sizeClass} shrink-0 overflow-hidden rounded-lg shadow-sm`}
      >
        <PersonaImage
          image={image}
          alt={name ?? "Character"}
          preferSmall
          className="h-full w-full"
        />
      </div>
    );
  }

  return (
    <Avatar className={`${sizeClass} shrink-0 rounded-lg`}>
      <AvatarFallback>{name?.slice(0, 2).toUpperCase() ?? "?"}</AvatarFallback>
    </Avatar>
  );
}

async function fetchAccountAnimal(
  accountId: string
): Promise<AccountAnimalPreview | null> {
  try {
    const snapshot = await getDocs(
      query(collection(db, "Account", accountId, "Animal"), limit(1))
    );
    if (snapshot.empty) return null;
    const animal = snapshot.docs[0].data() as Animal;
    const persona = getPersona(animal);
    const imageCollection = persona
      ? new PersonaImageCollection(persona)
      : null;
    return {
      name: animal.name || persona?.name,
      image: imageCollection?.getPoses("greeting")?.[0] ?? null,
    };
  } catch (error) {
    logException(error);
    return null;
  }
}

export function WebPurchaseRedemptionDialogContent({
  onClose,
}: WebPurchaseRedemptionDialogContentProps) {
  const {
    userData,
    user,
    accountId,
    defaultAccountId,
    switchAccount,
    signInWithGoogle,
    signInWithApple,
    logout,
  } = useAuth();
  const outcome = usePaymentsStore(
    (s) => s.webPurchaseRedemptionSession?.outcome ?? { kind: "idle" as const }
  );
  const isRedeeming = usePaymentsStore(
    (s) => s.webPurchaseRedemptionSession?.isRedeeming ?? false
  );
  const isReady = usePaymentsStore((s) => s.isReady);
  const [activeProvider, setActiveProvider] = useState<
    "Google" | "Apple" | null
  >(null);
  const [userAccounts, setUserAccounts] = useState<
    RedemptionAccountOption[] | null
  >(null);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [anonymousAnimal, setAnonymousAnimal] =
    useState<AccountAnimalPreview | null>(null);
  const [anonymousAnimalLoading, setAnonymousAnimalLoading] = useState(false);

  const isSignedIn = !!userData && user?.isAnonymous === false;
  const isAnonymousUser = !!user?.isAnonymous;
  const platform = getPlatform();
  const hasMultipleAccounts = (userAccounts?.length ?? 0) > 1;

  useEffect(() => {
    if (!isSignedIn || !user?.uid) {
      setUserAccounts(null);
      return;
    }

    let cancelled = false;
    setAccountsLoading(true);

    void (async () => {
      try {
        const accounts = await getUserAccounts(user.uid);
        const options = await Promise.all(
          accounts.map(async (account) => {
            const animal = await fetchAccountAnimal(account.ref.id);
            return {
              id: account.ref.id,
              name: account.name,
              email: account.email,
              animalImage: animal?.image,
            };
          })
        );
        if (!cancelled) setUserAccounts(options);
      } catch (error) {
        logException(error);
        if (!cancelled) setUserAccounts([]);
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, user?.uid]);

  useEffect(() => {
    if (!isAnonymousUser || !accountId) {
      setAnonymousAnimal(null);
      return;
    }

    let cancelled = false;
    setAnonymousAnimalLoading(true);

    void (async () => {
      try {
        const animal = await fetchAccountAnimal(accountId);
        if (!cancelled) setAnonymousAnimal(animal);
      } catch (error) {
        logException(error);
        if (!cancelled) setAnonymousAnimal(null);
      } finally {
        if (!cancelled) setAnonymousAnimalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAnonymousUser, accountId]);

  const handleSignIn = async (provider: "Google" | "Apple") => {
    setActiveProvider(provider);
    resetWebPurchaseRedemptionOutcome();
    try {
      if (provider === "Google") {
        await signInWithGoogle();
      } else {
        await signInWithApple();
      }
      sendConversionEvent("um_sign_in");
    } catch (error) {
      logException(error);
      console.error(`Error signing in with ${provider}:`, error);
    } finally {
      setActiveProvider(null);
    }
  };

  const handleSwitchUser = async () => {
    resetWebPurchaseRedemptionOutcome();
    await logout();
  };

  const handleLinkPurchase = () => {
    void redeemWebPurchaseInDialog();
  };

  const linkPurchaseButton = (
    <Button
      className="w-full"
      onClick={handleLinkPurchase}
      disabled={!isReady || isRedeeming}
    >
      {isRedeeming ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Linking purchase…
        </>
      ) : !isReady ? (
        "Preparing…"
      ) : (
        "Link purchase to this account"
      )}
    </Button>
  );

  const renderSignedInAccountSection = () => {
    if (accountsLoading) {
      return (
        <div className="flex items-center justify-center py-6 text-sm text-gray-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading accounts…
        </div>
      );
    }

    if (hasMultipleAccounts && userAccounts) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Choose which account to link your purchase to:
            </p>
            <button
              type="button"
              onClick={() => void handleSwitchUser()}
              className="shrink-0 text-sm text-blue-600 hover:text-blue-800"
            >
              Switch user
            </button>
          </div>
          <div className="max-h-60 space-y-2 overflow-y-auto">
            {userAccounts.map((account) => {
              const isSelected = account.id === accountId;
              const isDefault = account.id === defaultAccountId;
              return (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => switchAccount(account.id)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                    isSelected
                      ? "border-brand-secondary bg-brand-secondary/5 ring-1 ring-brand-secondary/30"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <AnimalPersonaImage
                    image={account.animalImage}
                    name={account.name}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="font-medium truncate">{account.name}</p>
                      {isDefault && (
                        <Star
                          className="h-4 w-4 shrink-0 text-yellow-400 fill-current"
                          aria-label="Default account"
                        />
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {account.email}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          {outcome.kind === "wrong_user" ? (
            <Button
              className="w-full"
              variant="outline"
              onClick={() => void handleSwitchUser()}
            >
              Sign in with a different user
            </Button>
          ) : (
            linkPurchaseButton
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">You are signed in as:</p>
          <button
            type="button"
            onClick={() => void handleSwitchUser()}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Switch user
          </button>
        </div>
        <div className="flex items-center space-x-4">
          {userAccounts?.[0]?.animalImage ? (
            <AnimalPersonaImage
              image={userAccounts[0].animalImage}
              name={userAccounts[0].name}
            />
          ) : (
            <Avatar className="h-12 w-12">
              {userData?.image?.small ? (
                <img src={userData.image.small} alt={userData.display_name} />
              ) : (
                <AvatarFallback>
                  {userData?.display_name?.slice(0, 2).toUpperCase() || "U"}
                </AvatarFallback>
              )}
            </Avatar>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="font-medium truncate">
                {userAccounts?.[0]?.name ?? userData?.display_name}
              </p>
              {accountId === defaultAccountId && (
                <Star
                  className="h-4 w-4 shrink-0 text-yellow-400 fill-current"
                  aria-label="Default account"
                />
              )}
            </div>
            <p className="text-sm text-gray-500 truncate">
              {userAccounts?.[0]?.email ?? userData?.email}
            </p>
          </div>
        </div>
        {outcome.kind === "wrong_user" ? (
          <Button
            className="w-full"
            variant="outline"
            onClick={() => void handleSwitchUser()}
          >
            Sign in with a different account
          </Button>
        ) : (
          linkPurchaseButton
        )}
      </div>
    );
  };

  const renderAnonymousAccountSection = () => {
    if (anonymousAnimalLoading) {
      return (
        <div className="flex items-center justify-center py-6 text-sm text-gray-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading…
        </div>
      );
    }

    const showAnimalPreview =
      !!anonymousAnimal?.name || !!anonymousAnimal?.image;

    return (
      <div className="space-y-4">
        {showAnimalPreview ? (
          <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <AnimalPersonaImage
              image={anonymousAnimal?.image}
              name={anonymousAnimal?.name}
              sizeClass="h-14 w-14"
            />
            {anonymousAnimal?.name ? (
              <p className="text-lg font-medium">{anonymousAnimal.name}</p>
            ) : null}
          </div>
        ) : null}

        <p className="text-sm text-gray-600">
          {anonymousAnimal?.name
            ? `Your subscription will be linked to ${anonymousAnimal.name}'s new account.`
            : "Your subscription will be linked to this new account."}{" "}
          You can sign in with Google or Apple later to access it on other
          devices.
        </p>

        {linkPurchaseButton}

        <Button
          variant="destructive"
          className="w-full"
          onClick={() => void handleSwitchUser()}
        >
          Abandon this account and sign in
        </Button>
      </div>
    );
  };

  const renderSignInSection = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Sign in to link your purchase to your account.
      </p>
      <div className="w-full space-y-3">
        <Button
          variant="outline"
          className="w-full h-11 bg-white/70 backdrop-blur-sm hover:bg-white/90 flex items-center justify-center"
          onClick={() => void handleSignIn("Google")}
          disabled={!!activeProvider}
        >
          <GoogleIcon className="mr-2 h-5 w-5" />
          {activeProvider === "Google" ? "Processing…" : "Sign in with Google"}
        </Button>
        {platform !== "android" && (
          <Button
            variant="default"
            className="w-full h-11 bg-black text-white hover:bg-black/90 flex items-center justify-center gap-0"
            onClick={() => void handleSignIn("Apple")}
            disabled={!!activeProvider}
          >
            <img src="/assets/AppleLogo.svg" alt="" />
            {activeProvider === "Apple" ? "Processing…" : "Sign in with Apple"}
          </Button>
        )}
        <TestLoginButton onSuccess={() => sendConversionEvent("um_sign_in")} />
      </div>

      <p className="text-xs text-muted-foreground text-center">
        By continuing, you agree to our{" "}
        <Link
          to="/terms"
          className="underline text-blue-600 hover:text-blue-800"
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          to="/privacy"
          className="underline text-blue-600 hover:text-blue-800"
        >
          Privacy Policy
        </Link>
      </p>
    </div>
  );

  if (outcome.kind === "success") {
    return (
      <div className="space-y-4">
        <DialogHeader>
          <h2 className="text-lg leading-none font-semibold">Purchase linked</h2>
          <p className="text-muted-foreground text-sm">
            Your web purchase is now linked to this account. You can access your
            subscription in the app.
          </p>
        </DialogHeader>
        <Button className="w-full" onClick={onClose}>
          Continue
        </Button>
      </div>
    );
  }

  if (outcome.kind === "expired") {
    return (
      <div className="space-y-4">
        <DialogHeader>
          <h2 className="text-lg leading-none font-semibold">
            This link has expired
          </h2>
          <p className="text-muted-foreground text-sm">
            Redemption links are valid for 60 minutes. A new link has been sent
            to your email
            {outcome.obfuscatedEmail ? (
              <>
                {" "}
                at{" "}
                <span className="font-medium">{outcome.obfuscatedEmail}</span>
              </>
            ) : null}
            . Open the new link on this device to finish linking your purchase.
          </p>
        </DialogHeader>
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
          <Mail
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-700"
            aria-hidden
          />
          <p>Check your inbox (and spam folder) for a fresh redemption link.</p>
        </div>
        <Button className="w-full" variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  if (outcome.kind === "invalid") {
    return (
      <div className="space-y-4">
        <DialogHeader>
          <h2 className="text-lg leading-none font-semibold">
            Invalid redemption link
          </h2>
          <p className="text-muted-foreground text-sm">
            This link is not valid. Open the redemption link from your purchase
            confirmation email on this device, or request a new one from your
            purchase email.
          </p>
        </DialogHeader>
        <Button className="w-full" variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  const wrongUserBanner =
    outcome.kind === "wrong_user" ? (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        This purchase belongs to another account. Sign in with the account you
        used at checkout.
      </div>
    ) : outcome.kind === "error" ? (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {outcome.message}
      </div>
    ) : null;

  return (
    <div className="space-y-5">
      <DialogHeader className="space-y-2 text-left">
        <img
          src="/assets/brain-flower-logo-words-wide.svg"
          alt="Unique Minds"
          className="h-10 w-auto"
        />
        <h2 className="text-lg leading-none font-semibold">
          Link your web purchase
        </h2>
        <p className="text-muted-foreground text-sm">
          Connect the subscription you bought on the web to your Unique Minds
          account in this app.
        </p>
      </DialogHeader>

      {wrongUserBanner}

      {isSignedIn
        ? renderSignedInAccountSection()
        : isAnonymousUser
          ? renderAnonymousAccountSection()
          : renderSignInSection()}
    </div>
  );
}
